/* eslint-disable max-lines */
import { animated, useSpring } from "@react-spring/web";
import { useAtom } from "jotai";
import { Loader, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { useDtfPricing } from "@design-lab/entities/artwork";
import { isRecaptureTaskPendingAtom } from "@design-lab/features/user-retention";
import { useDesignLabConfig } from "@design-lab/shared/lib/config";
import { formatCurrency } from "@design-lab/shared/lib/currency";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { highlightJiffyDesignServiceAtom } from "@design-lab/shared/state";
import { Badge, Checkbox, Label, Switch, UnionIcon } from "@design-lab/shared/ui";
import { SwitchInput } from "@design-lab/shared/ui/SwitchInput";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { REDRAW_NOTES_MAX_LENGTH } from "common/redraw-constants";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { DesignServiceInfo } from "../DesignServiceInfo";
import { PrintPerfectExplanatoryTooltip } from "./PrintPerfectExplanatoryTooltip";

export type PrintPerfectProps = {
  canRemoveBackground?: boolean;
  showRefineArtwork?: boolean;
  canEditCutoutModal?: boolean;
  // Form-related props
  removeBackground: boolean;
  jiffyDesignServices: boolean;
  enhancementRequestNote?: string | undefined;
  editImageManual: boolean;
  instructionsError?: string | undefined;
  placementSide?: "front" | "back";
  // Handlers
  onRemoveBackgroundChange: (checked: boolean) => void;
  onJiffyDesignServicesChange: (checked: boolean) => void;
  onEnhancementRequestNoteChange: (value: string) => void;
  onRefineArtworkClick?: undefined | (() => void);
  onResetManualEdit?: undefined | (() => void);
  // Form registration for the textarea
  enhancementRequestNoteField: any;
  // Lens for SwitchInput (optional - only when used in design lab)
  removeBackgroundLens?: any;
};

// eslint-disable-next-line max-lines-per-function
export function PrintPerfect({
  canRemoveBackground = true,
  showRefineArtwork = true,
  removeBackground,
  jiffyDesignServices,
  editImageManual,
  instructionsError,
  onRemoveBackgroundChange,
  onJiffyDesignServicesChange,
  onRefineArtworkClick,
  onResetManualEdit,
  enhancementRequestNoteField,
  removeBackgroundLens,
  enhancementRequestNote
}: PrintPerfectProps) {
  const [isConfirmToggleOpen, setIsConfirmToggleOpen] = useState(false);
  const [isConfirmResetOpen, setIsConfirmResetOpen] = useState(false);
  const jiffyDesignServicesId = useId();
  const designerInstructionsId = useId();
  const { data: pricingData } = useDtfPricing();
  const expertEditAddonTierPrice = pricingData?.expertEditAddonTierPrice;
  const [shouldHighlight, setShouldHighlight] = useAtom(highlightJiffyDesignServiceAtom);
  const jiffyDesignServicesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const config = useDesignLabConfig();
  const isCustomerReviewOnRedrawsEnabled =
    config?.flip?.customerReviewOnRedraws && config?.flip.customerReviewOnPrintRedraws;

  const scrollToTextAreaAndHighlight = useCallback(() => {
    const $textarea = textareaRef.current;

    if ($textarea) {
      $textarea.scrollIntoView({ behavior: "smooth", block: "center" });
      $textarea.focus();
      setTimeout(() => {
        $textarea.classList.add("animate-highlight-pulse");
      }, 100);
    }
  }, []);

  // Shimmer animation - declarative approach (repeats 3 times)
  const shimmerStyles = useSpring({
    from: { x: -100, opacity: 0 },
    to: shouldHighlight
      ? [
          // First cycle
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } },
          // Second cycle
          { x: -100, opacity: 0, config: { duration: 100 } },
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } },
          // Third cycle
          { x: -100, opacity: 0, config: { duration: 100 } },
          { x: -30, opacity: 1, config: { duration: 200 } },
          { x: 80, opacity: 1, config: { duration: 1000 } },
          { x: 100, opacity: 0, config: { duration: 100 } }
        ]
      : { x: -50, opacity: 0 },
    onRest: () => {
      if (shouldHighlight) {
        setShouldHighlight(false);
      }
    },
    config: { tension: 280, friction: 60 }
  });

  // Handle scrolling when highlight is triggered
  useEffect(() => {
    if (!shouldHighlight) return;

    jiffyDesignServicesRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }, [shouldHighlight]);

  const [isRecaptureTaskPending, setRecaptureTaskPending] = useAtom(isRecaptureTaskPendingAtom);
  const pendingScrollRef = useRef(false);

  // Handle pending recapture task when this component renders (after tab switch)
  useEffect(() => {
    if (!isRecaptureTaskPending) return;

    setRecaptureTaskPending(false);

    if (jiffyDesignServices) {
      scrollToTextAreaAndHighlight();
    } else {
      onJiffyDesignServicesChange(true);
      pendingScrollRef.current = true;
    }
  }, [
    isRecaptureTaskPending,
    setRecaptureTaskPending,
    onJiffyDesignServicesChange,
    jiffyDesignServices,
    scrollToTextAreaAndHighlight
  ]);

  const textareaCallbackRef = useCallback(
    ($node: HTMLTextAreaElement | null) => {
      textareaRef.current = $node;
      enhancementRequestNoteField?.ref?.($node);

      if ($node && pendingScrollRef.current) {
        pendingScrollRef.current = false;
        scrollToTextAreaAndHighlight();
      }
    },
    [enhancementRequestNoteField, scrollToTextAreaAndHighlight]
  );

  const formattedPrice =
    expertEditAddonTierPrice > 0 ? formatCurrency(expertEditAddonTierPrice) : "Free";

  return (
    <div className="flex flex-col gap-4">
      {canRemoveBackground && (
        <div className="flex items-center space-x-2">
          {removeBackgroundLens ? (
            <SwitchInput
              data-testid="remove-background-switch"
              id="remove-background"
              lens={removeBackgroundLens}
              onCheckedChange={checked => {
                // Show confirmation if there are manual edits that will be cleared
                if (editImageManual) {
                  setIsConfirmToggleOpen(true);
                  return;
                }
                onRemoveBackgroundChange(checked);
              }}
            />
          ) : (
            <Switch
              data-testid="remove-background-switch"
              id="remove-background"
              isSelected={removeBackground}
              onChange={checked => {
                // Show confirmation if there are manual edits that will be cleared
                if (editImageManual) {
                  setIsConfirmToggleOpen(true);
                  return;
                }
                onRemoveBackgroundChange(checked);
              }}
            />
          )}
          <Label data-testid="remove-background-label" htmlFor="remove-background">
            Remove background
          </Label>
        </div>
      )}

      {showRefineArtwork && (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="refine-artwork-button"
            className="bg-cta-gradient hover:bg-cta-gradient-hover flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border-0 p-3 text-sm text-white"
            onClick={onRefineArtworkClick}
          >
            <UnionIcon className="h-4 w-4 text-white" />
            <span>Edit Design</span>
          </button>
          {editImageManual && (
            <button
              type="button"
              data-testid="reset-manual-edit-button"
              className="ml-3 flex cursor-pointer items-center gap-1 text-sm text-blue-700 hover:text-blue-800"
              onClick={() => setIsConfirmResetOpen(true)}
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reset</span>
            </button>
          )}
        </div>
      )}

      <div
        ref={jiffyDesignServicesRef}
        className={cn(
          "relative flex items-center justify-between space-x-2 overflow-hidden rounded-lg bg-green-200 p-3 transition-all duration-300",
          shouldHighlight && "shadow-xl shadow-green-300/50 ring-2 ring-green-300"
        )}
      >
        {/* Primary shimmer element */}
        <animated.span
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
          style={{
            transform: shimmerStyles.x.to(x => `translateX(${x}%)`),
            opacity: shimmerStyles.opacity
          }}
        />
        {/* Secondary glow layer for extra vibrancy */}
        <animated.span
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-green-100/50 to-transparent blur-xl"
          style={{
            transform: shimmerStyles.x.to(x => `translateX(${x}%)`),
            opacity: shimmerStyles.opacity.to(o => o * 0.7)
          }}
        />
        <div className="flex flex-1 items-center space-x-2">
          <Checkbox
            data-testid="jiffy-design-services-switch"
            id={jiffyDesignServicesId}
            isSelected={jiffyDesignServices || false}
            onChange={checked => {
              const isChecked = checked === true;
              onJiffyDesignServicesChange(isChecked);
            }}
          />
          <div className="flex flex-col gap-2 lg:flex-row">
            <Label
              data-testid="jiffy-design-services-label"
              htmlFor={jiffyDesignServicesId}
              className="order-2 flex items-center gap-2 lg:order-1"
            >
              Jiffy Design Services
            </Label>
            {expertEditAddonTierPrice ? (
              <>
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      "order-1 items-center gap-2 lg:order-2",
                      index === 0 ? "hidden lg:flex" : "flex lg:hidden"
                    )}
                  >
                    <Badge size="sm" className="bg-gray-700 font-medium">
                      Add-On
                    </Badge>
                    <p className="whitespace-nowrap text-sm text-green-700">{formattedPrice}</p>
                  </div>
                ))}
              </>
            ) : (
              <Loader className="animate-spin" />
            )}
          </div>
          <div className="flex flex-1 justify-end">
            <PrintPerfectExplanatoryTooltip>
              <DesignServiceInfo />
            </PrintPerfectExplanatoryTooltip>
          </div>
        </div>
      </div>

      {jiffyDesignServices && (
        <div className="flex flex-col gap-2">
          <Label data-testid="designer-instructions-label" htmlFor={designerInstructionsId}>
            <em className="text-red-500">*</em>&nbsp; Instructions for our designers
          </Label>
          <Textarea
            data-testid="designer-instructions-textarea"
            {...enhancementRequestNoteField}
            ref={textareaCallbackRef}
            id={designerInstructionsId}
            rows={4}
            maxLength={REDRAW_NOTES_MAX_LENGTH}
            placeholder='Example: "Change the color of the font to black. Remove the background from the top half of the image."'
            className={cn(instructionsError && "border-red-500")}
          />
          {instructionsError && <small className="text-red-500">{instructionsError}</small>}
          {enhancementRequestNote && isCustomerReviewOnRedrawsEnabled && (
            <span className="text-sm font-normal leading-5">
              <b>Note: </b>
              We will send a notification to review your redrawn artwork before printing. If there
              is no response within 1 hour, your order will proceed to print to prevent any further
              delays.
            </span>
          )}
        </div>
      )}
      <ConfirmActionModal
        isOpen={isConfirmToggleOpen}
        onClose={() => setIsConfirmToggleOpen(false)}
        title={`Turn ${removeBackground ? "Off" : "On"} Background Removal?`}
        message={`${removeBackground ? "Disabling" : "Enabling"} this will undo all background edits you've made. Do you want to continue?`}
        confirmText={`Turn ${removeBackground ? "Off" : "On"}`}
        cancelText="Keep Edits"
        onConfirm={() => {
          onRemoveBackgroundChange(!removeBackground);
          setIsConfirmToggleOpen(false);
        }}
      />
      <ConfirmActionModal
        isOpen={isConfirmResetOpen}
        onClose={() => setIsConfirmResetOpen(false)}
        title="Reset Edits?"
        message="This will discard all manual edits and restore the original image. Continue?"
        confirmText="Reset"
        cancelText="Keep Edits"
        onConfirm={() => {
          onResetManualEdit?.();
          setIsConfirmResetOpen(false);
        }}
      />
    </div>
  );
}
